class CartNotification extends HTMLElement {
  constructor() {
    super();
    this.notification = this.querySelector('#cart-notification');
    this.productContainer = this.querySelector('#cart-notification-product');
    this.summaryContainer = this.querySelector('#cart-notification-button');
    this.statusRegion = this.querySelector('#cart-notification-status');
    this.errorRegion = this.querySelector('#cart-notification-error');
    this.headingText = this.querySelector('[data-cart-notification-heading]');
    this.successMark = this.querySelector('.cart-notification__success-mark');
    this.dismissDelay = 3000;
    this.dismissRemaining = this.dismissDelay;
    this.pauseReasons = new Set();
    this.requestSequence = 0;
    this.latestRenderedSequence = 0;
    this.pendingRequests = new Set();
    this.cartRequestQueue = Promise.resolve();
    this.needsAuthoritativeRefresh = false;
    this.closeTimer = null;
    this.dismissTimer = null;
    this.dismissStartedAt = 0;
    this.authoritativeRefreshController = null;
    this.refreshShouldOpen = false;
    this.activeElementToRestore = null;
    this.abortController = null;
  }

  connectedCallback() {
    if (!this.notification || this.abortController) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.querySelectorAll('[data-cart-notification-close]').forEach((button) => {
      button.addEventListener('click', () => this.close(), { signal });
    });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen()) this.close();
    }, { signal });
    this.notification.addEventListener('pointerenter', (event) => {
      if (event.pointerType === 'mouse') this.pauseDismissal('hover');
    }, { signal });
    this.notification.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'mouse') this.resumeDismissal('hover');
    }, { signal });
    this.notification.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch') this.pauseDismissal('touch');
    }, { signal, passive: true });
    document.addEventListener('pointerup', () => this.resumeDismissal('touch'), { signal, passive: true });
    document.addEventListener('pointercancel', () => this.resumeDismissal('touch'), { signal, passive: true });
    this.notification.addEventListener('focusin', () => this.pauseDismissal('focus'), { signal });
    this.notification.addEventListener('focusout', () => {
      window.setTimeout(() => {
        if (!this.notification.contains(document.activeElement)) this.resumeDismissal('focus');
      });
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.pauseDismissal('visibility');
      else this.resumeDismissal('visibility');
    }, { signal });
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.authoritativeRefreshController?.abort();
    this.abortController = null;
    this.clearTimers();
  }

  beginAdd(originatingElement) {
    const requestToken = ++this.requestSequence;
    this.authoritativeRefreshController?.abort();
    this.refreshShouldOpen = false;
    if (this.pendingRequests.size) this.needsAuthoritativeRefresh = true;
    this.pendingRequests.add(requestToken);
    this.setActiveElement(originatingElement);
    if (this.notification.classList.contains('is-error')) {
      this.notification.classList.remove('active', 'is-error');
      this.notification.setAttribute('aria-hidden', 'true');
      this.notification.setAttribute('inert', '');
      this.notification.setAttribute('hidden', '');
    }
    this.clearError();
    this.pauseDismissal('request');
    return requestToken;
  }

  queueCartRequest(requestFactory) {
    const request = this.cartRequestQueue.then(requestFactory, requestFactory);
    this.cartRequestQueue = request.catch(() => undefined);
    return request;
  }

  finishAdd(requestToken) {
    if (requestToken) this.pendingRequests.delete(requestToken);
    if (this.pendingRequests.size) return;
    this.resumeDismissal('request');
    if (this.needsAuthoritativeRefresh) {
      this.needsAuthoritativeRefresh = false;
      this.refreshAuthoritativeSections({ requestToken: this.requestSequence });
    }
  }

  renderContents(parsedState, requestToken = ++this.requestSequence) {
    if (!parsedState || requestToken < this.latestRenderedSequence) return;
    const addedItems = Array.isArray(parsedState.items) ? parsedState.items : [parsedState];
    const addedItem = addedItems[addedItems.length - 1] || {};
    this.latestRenderedSequence = requestToken;
    this.latestAddedKey = addedItem.key || null;
    this.latestAddedVariantId = String(addedItem.variant_id || addedItem.id || '');
    const update = this.updateSections(parsedState.sections || {});
    if (!update.product || !update.summary) {
      this.refreshAuthoritativeSections({ requestToken, openAfterRefresh: true });
      return;
    }
    this.clearError();
    this.open();
    this.announceSuccess();
  }

  updateSections(sections) {
    const result = { product: false, summary: false };
    const productMarkup = sections['cart-notification-product'];
    if (this.productContainer && productMarkup) {
      const source = new DOMParser().parseFromString(productMarkup, 'text/html');
      const products = [...source.querySelectorAll('[data-cart-notification-product]')];
      const selectedProduct = products.find((product) => product.dataset.cartLineKey === this.latestAddedKey)
        || products.find((product) => product.dataset.variantId === this.latestAddedVariantId)
        || products[0];
      this.productContainer.innerHTML = selectedProduct?.outerHTML || '';
      result.product = Boolean(selectedProduct);
    }

    ['cart-notification-button', 'cart-icon-bubble'].forEach((sectionId) => {
      const target = document.getElementById(sectionId);
      const markup = sections[sectionId];
      if (!target || !markup) return;
      const replacement = this.getSectionInnerHTML(markup);
      if (replacement !== null) target.innerHTML = replacement;
      if (sectionId === 'cart-notification-button' && replacement !== null) result.summary = true;
    });
    return result;
  }

  async refreshAuthoritativeSections({ requestToken = this.requestSequence, openAfterRefresh = false } = {}) {
    this.refreshShouldOpen = this.refreshShouldOpen || openAfterRefresh;
    this.authoritativeRefreshController?.abort();
    const controller = new AbortController();
    this.authoritativeRefreshController = controller;
    const sectionIds = this.getSectionsToRender().map((section) => section.id);
    try {
      const separator = routes.cart_url.includes('?') ? '&' : '?';
      const response = await fetch(`${routes.cart_url}${separator}sections=${sectionIds.join(',')}`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });
      if (!response.ok) return;
      const sections = await response.json();
      if (controller !== this.authoritativeRefreshController || requestToken !== this.requestSequence) return;
      const update = this.updateSections(sections);
      if (this.refreshShouldOpen && update.product && update.summary) {
        this.clearError();
        this.open();
      }
      if (this.isOpen() && !this.notification.classList.contains('is-error')) this.announceSuccess();
      this.refreshShouldOpen = false;
    } catch (error) {
      // A successful add remains visible; a later cart refresh will reconcile it.
    } finally {
      if (controller === this.authoritativeRefreshController) this.authoritativeRefreshController = null;
    }
  }

  open() {
    window.clearTimeout(this.closeTimer);
    const wasOpen = this.isOpen();
    this.notification.removeAttribute('hidden');
    this.notification.removeAttribute('inert');
    this.notification.setAttribute('aria-hidden', 'false');
    this.notification.classList.remove('is-closing', 'is-error');
    if (!wasOpen) void this.notification.offsetWidth;
    this.notification.classList.add('active');
    this.restartSuccessMark();
    this.startDismissal();
  }

  close() {
    if (!this.isOpen() && this.notification.hasAttribute('hidden')) return;
    const focusWasInside = this.notification.contains(document.activeElement);
    this.clearDismissTimer();
    this.notification.classList.remove('active');
    this.notification.classList.add('is-closing');
    this.notification.setAttribute('aria-hidden', 'true');
    this.notification.setAttribute('inert', '');
    ['hover', 'focus', 'touch'].forEach((reason) => this.pauseReasons.delete(reason));
    if (focusWasInside && this.activeElementToRestore?.isConnected) {
      this.activeElementToRestore.focus({ preventScroll: true });
    }
    window.clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      if (this.notification.classList.contains('active')) return;
      this.notification.setAttribute('hidden', '');
      this.notification.classList.remove('is-closing');
      this.clearError();
    }, this.prefersReducedMotion() ? 0 : 320);
  }

  showError(message, requestToken = ++this.requestSequence) {
    if (requestToken < this.latestRenderedSequence || !message) return;
    this.latestRenderedSequence = requestToken;
    this.clearDismissTimer();
    this.productContainer.innerHTML = '';
    this.summaryContainer.innerHTML = '';
    this.headingText.textContent = 'Unable to add item';
    this.successMark.removeAttribute('hidden');
    this.restartSuccessMark();
    this.errorRegion.textContent = message;
    this.errorRegion.removeAttribute('hidden');
    this.notification.classList.add('is-error');
    window.clearTimeout(this.closeTimer);
    this.notification.removeAttribute('hidden');
    this.notification.removeAttribute('inert');
    this.notification.setAttribute('aria-hidden', 'false');
    void this.notification.offsetWidth;
    this.notification.classList.add('active');
    this.startDismissal(4000);
  }

  clearError() {
    if (!this.errorRegion) return;
    this.headingText.textContent = 'Added to your selection';
    this.successMark.removeAttribute('hidden');
    this.errorRegion.textContent = '';
    this.errorRegion.setAttribute('hidden', '');
    this.notification?.classList.remove('is-error');
  }

  startDismissal(delay = this.dismissDelay) {
    this.clearDismissTimer();
    this.dismissRemaining = delay;
    this.scheduleDismissal();
  }

  scheduleDismissal() {
    if (!this.isOpen() || this.pauseReasons.size) return;
    this.dismissStartedAt = performance.now();
    this.dismissTimer = window.setTimeout(() => this.close(), this.dismissRemaining);
  }

  pauseDismissal(reason) {
    if (this.pauseReasons.has(reason)) return;
    this.pauseReasons.add(reason);
    if (!this.dismissTimer) return;
    this.dismissRemaining = Math.max(0, this.dismissRemaining - (performance.now() - this.dismissStartedAt));
    this.clearDismissTimer();
  }

  resumeDismissal(reason) {
    this.pauseReasons.delete(reason);
    if (!this.pauseReasons.size) this.scheduleDismissal();
  }

  clearDismissTimer() {
    window.clearTimeout(this.dismissTimer);
    this.dismissTimer = null;
  }

  clearTimers() {
    this.clearDismissTimer();
    window.clearTimeout(this.closeTimer);
  }

  restartSuccessMark() {
    if (!this.successMark) return;
    this.successMark.classList.remove('is-drawing');
    void this.successMark.offsetWidth;
    this.successMark.classList.add('is-drawing');
  }

  announceSuccess() {
    if (!this.statusRegion) return;
    const productName = this.productContainer.querySelector('.cart-notification-product__name')?.textContent?.trim();
    const bagSummary = this.summaryContainer.querySelector('.cart-notification__summary')?.textContent?.replace(/\s+/g, ' ')?.trim();
    this.statusRegion.textContent = `${productName ? `${productName} added to your selection.` : 'Item added to your selection.'}${bagSummary ? ` ${bagSummary}` : ''}`;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-notification-product' },
      { id: 'cart-notification-button' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  setActiveElement(element) {
    if (element instanceof HTMLElement) this.activeElementToRestore = element;
  }

  isOpen() {
    return this.notification?.classList.contains('active');
  }

  prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('cart-notification')) customElements.define('cart-notification', CartNotification);
